/**
 * The whole note form: fields, Cancel and Save. One place for the shell that "Save note" in
 * the Inbox, adding and editing a note on the Notes card, and the edit popup all wore their own
 * copy of — `NoteFields` already shared the fields, but the form around them (the grid, the
 * button row, the busy label, the title rule) had been pasted three times and had begun to
 * differ (#82).
 */

import type { Area, CaptureTier, Note, Project } from "../api";
import { describeRefined, type AiRefined } from "../aiRefine";
import { CaptureTierPicker } from "./CaptureTierPicker";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { NoteFields } from "./NoteFields";
import { noteAiTitle, type NoteFieldValues } from "./noteFieldValues";

export function NoteForm({
  values,
  onChange,
  areas,
  projects,
  busy,
  submitLabel = "Save",
  requireTitle = true,
  autoFocus = true,
  onSubmit,
  onCancel,
  onDelete,
  onAi,
  busyLabel = "Saving…",
  error,
  editAi,
}: {
  values: NoteFieldValues;
  onChange: (patch: Partial<NoteFieldValues>) => void;
  areas: Area[];
  projects: Project[];
  busy: boolean;
  submitLabel?: string;
  /** The Inbox form may leave the title empty — it falls back to the item's own text. */
  requireTitle?: boolean;
  autoFocus?: boolean;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
  onDelete?: () => void | Promise<void>;
  /** Shows the AI bulb beside Save. Pressing it hands the form's current values to the AI (the
   * owner decides what that does: the bulb is the same one everywhere a note form appears). */
  onAi?: () => void;
  /** What `busy` reads as on the submit button - "Asking the AI…" while the bulb's call runs. */
  busyLabel?: string;
  /** Shown under the buttons, for an AI call that did not go through. */
  error?: string | null;
  /** The AI bulb of an editor, for a note that already exists: unlike `onAi` it waits in place. The
   * other buttons fold away while it works, an answer box draws out of the bulb saying what the AI
   * did, and the answer clears itself (as the Inbox's does) by closing the form. `onRetry` makes the
   * request and resolves with the updated note; `keepOpen` is true once the form has been changed
   * since the AI answered, which gives the buttons back. */
  editAi?: {
    onRetry: (tier: CaptureTier | null) => Promise<AiRefined<Note>>;
    onSettled: (result: AiRefined<Note> | null) => void | Promise<void>;
    keepOpen: boolean;
  };
}) {
  return (
    <form
      data-ai-host={editAi ? "" : undefined}
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit();
      }}
      style={{ display: "grid", gap: "0.55rem", width: "100%" }}
    >
      <NoteFields
        values={values}
        onChange={onChange}
        areas={areas}
        projects={projects}
        autoFocus={autoFocus}
      />
      <div className="row wrap">
        {onDelete && (
          <button
            type="button"
            className={`btn ghost small${editAi ? " inbox-fold" : ""}`}
            onClick={() => void onDelete()}
            disabled={busy}
          >
            Delete
          </button>
        )}
        <span className="ai-spacer" style={{ flex: 1 }} />
        <button
          type="button"
          className={`btn ghost small${editAi ? " inbox-fold" : ""}`}
          onClick={onCancel}
        >
          Cancel
        </button>
        {editAi && (
          <InboxAiRetryButton<AiRefined<Note>>
            surface="note-form"
            label="Tidy up with AI"
            busyLabel="Asking the AI"
            doneLabel="Close - the AI's changes are saved"
            disabled={busy || !noteAiTitle(values)}
            keepOpen={editAi.keepOpen}
            describe={describeRefined}
            onRetry={editAi.onRetry}
            onSettled={editAi.onSettled}
            leaveOnClear
          />
        )}
        {onAi && (
          <CaptureTierPicker
            surface="note-form"
            onPress={onAi}
            pressLabel="Tidy up with AI"
            pressDisabled={busy || !noteAiTitle(values)}
          />
        )}
        <button
          className={`btn primary small${editAi ? " inbox-fold" : ""}`}
          type="submit"
          disabled={busy || (requireTitle && !values.title.trim())}
        >
          {busy ? busyLabel : submitLabel}
        </button>
      </div>
      {error && (
        <p className="error small" style={{ margin: 0 }} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
